import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui';
import { cn } from '@/lib/utils';

export type IframeNoticeReason = 'sessionExpired' | 'loginFailed';

interface IframeNoticeScreenProps {
  reason: IframeNoticeReason;
  /** The Creator held edits that could not be saved any more. */
  unsavedChanges?: boolean;
  /**
   * Lay the notice over the page instead of replacing it, so the Creator
   * keeps its canvas mounted underneath.
   */
  overlay?: boolean;
}

const COPY: Record<
  IframeNoticeReason,
  { eyebrow: string; title: string; text: string }
> = {
  sessionExpired: {
    eyebrow: 'seller.session.expiredEyebrow',
    title: 'seller.session.expiredTitle',
    text: 'seller.session.expiredText',
  },
  loginFailed: {
    eyebrow: 'seller.session.loginFailedEyebrow',
    title: 'seller.session.loginFailedTitle',
    text: 'seller.session.loginFailedText',
  },
};

/**
 * Full-page notice for the two dead ends inside a shop's iframe
 * (docs/design/seller-area/SessionExpired.dc.html, LoginFailed.dc.html).
 * There is no login page in here and nothing to retry: the only way on is
 * to reopen the page from the shop, so the notice names that and nothing
 * else.
 */
export function IframeNoticeScreen({
  reason,
  unsavedChanges = false,
  overlay = false,
}: IframeNoticeScreenProps) {
  const { t } = useTranslation();
  const copy = COPY[reason];

  return (
    <div
      className={cn(
        'flex items-center justify-center bg-paper px-6 py-10',
        overlay ? 'fixed inset-0 z-50' : 'min-h-screen'
      )}
    >
      <Card
        variant="section"
        padding="none"
        role="alert"
        className="flex w-full max-w-[520px] flex-col items-center gap-4 px-10 py-11 text-center"
      >
        <div
          className="flex h-14 w-14 items-center justify-center rounded-pill bg-paper text-ink ring-1 ring-line"
          aria-hidden="true"
        >
          {reason === 'sessionExpired' ? <LockIcon /> : <AlertIcon />}
        </div>
        <p className="dc-eyebrow">{t(copy.eyebrow)}</p>
        <h1 className="dc-h1 text-[30px] text-ink">{t(copy.title)}</h1>
        <p className="text-[15px] text-ink-2">{t(copy.text)}</p>
        {unsavedChanges && (
          <p className="max-w-[420px] rounded-chip bg-paper px-3 py-2 text-[11.5px] font-semibold text-warning ring-1 ring-inset ring-warning">
            {t('seller.session.expiredUnsaved')}
          </p>
        )}
        <p className="inline-flex h-8 items-center rounded-control px-3 font-display text-[12.5px] font-semibold text-ink ring-1 ring-inset ring-line">
          {t('seller.session.hint')}
        </p>
        <p className="dc-mono mt-2 text-xs text-mute">
          {t('seller.session.footer')}
        </p>
      </Card>
    </div>
  );
}

function LockIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <rect width="18" height="11" x="3" y="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

function AlertIcon() {
  return (
    <svg
      width="26"
      height="26"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.75"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3" />
      <path d="M12 9v4" />
      <path d="M12 17h.01" />
    </svg>
  );
}
